import React, { useState, useEffect } from 'react';
import { Button, Col, Row, Modal, Space, Input, Form } from 'antd';
import { GoogleMap, Marker, Autocomplete } from '@react-google-maps/api';
import _ from 'lodash';

const defaultCoordinates = {
	lat: 22.3235253,
	lng: 114.1805002
};

const ModalMap = props => {
	const [form] = Form.useForm();

	const [currentMarked, setCurrentMarked] = useState(null);
	const [map, setMap] = useState(null);
	const [centerCoordinates, setCenterCoordinates] = useState(defaultCoordinates);
	const [address, setAddress] = useState('');
	const [autocomplete, setAutocomplete] = useState(null);

	const childElement = document.querySelector('.pac-target-input');
	if (childElement) {
		const parentElement = childElement.parentElement;
		if (parentElement) {
			parentElement.style.width = '100%';
		}
	}

	const displayOriginMark =
		!currentMarked &&
		props.routeType === 'origin' &&
		props.tripRoutes.length > 0 &&
		!_.isNil(props.activeIndex) &&
		!_.isNil(props.tripRoutes[props.activeIndex]) &&
		!_.isNil(props.tripRoutes[props.activeIndex].origin) &&
		props.tripRoutes[props.activeIndex].origin.length > 0 &&
		!_.isNil(props.tripRoutes[props.activeIndex].origin[0]);

	const displayDestinationMark =
		!currentMarked &&
		props.routeType === 'destination' &&
		props.tripRoutes.length > 0 &&
		!_.isNil(props.activeIndex) &&
		!_.isNil(props.tripRoutes[props.activeIndex]) &&
		!_.isNil(props.tripRoutes[props.activeIndex].destination) &&
		props.tripRoutes[props.activeIndex].destination.length > 0 &&
		!_.isNil(props.tripRoutes[props.activeIndex].destination[0]);

	useEffect(() => {
		if (props.isModalOpen) {
			if (currentMarked) {
				setCenterCoordinates({ lat: currentMarked.lat, lng: currentMarked.lng });
			} else if (
				props.routeType === 'origin' &&
				props.tripRoutes.length > 0 &&
				!_.isNil(props.activeIndex) &&
				!_.isNil(props.tripRoutes[props.activeIndex]) &&
				!_.isNil(props.tripRoutes[props.activeIndex].origin) &&
				props.tripRoutes[props.activeIndex].origin.length > 0 &&
				!_.isNil(props.tripRoutes[props.activeIndex].origin[0])
			) {
				setCenterCoordinates({ lat: props.tripRoutes[props.activeIndex].origin[0].lat, lng: props.tripRoutes[props.activeIndex].origin[0].lng });
			} else if (
				props.routeType === 'destination' &&
				props.tripRoutes.length > 0 &&
				!_.isNil(props.activeIndex) &&
				!_.isNil(props.tripRoutes[props.activeIndex]) &&
				!_.isNil(props.tripRoutes[props.activeIndex].destination) &&
				props.tripRoutes[props.activeIndex].destination.length > 0 &&
				!_.isNil(props.tripRoutes[props.activeIndex].destination[0])
			) {
				setCenterCoordinates({ lat: props.tripRoutes[props.activeIndex].destination[0].lat, lng: props.tripRoutes[props.activeIndex].destination[0].lng });
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.isModalOpen, currentMarked]);

	const onUnmount = React.useCallback(function callback(map) {
		setMap(null);
	}, []);

	const handleMapClick = e => {
		if (map) {
			const geocoder = new window.google.maps.Geocoder();
			const latLng = {
				lat: e.latLng.lat(),
				lng: e.latLng.lng()
			};

			// First set the bounds for autocomplete (if needed)
			if (autocomplete) {
				const bounds = {
					north: latLng.lat + 0.2,
					south: latLng.lat - 0.2,
					east: latLng.lng + 0.2,
					west: latLng.lng - 0.2
				};
				autocomplete.setBounds(bounds);
			}

			// Get place information using geocoder
			geocoder.geocode({ location: latLng }, (results, status) => {
				if (status === 'OK' && results[0]) {
					console.log('Place details:', results[0]);
					// Handle the place information here
					const address = results[0].name || results[0].formatted_address;
					setCurrentMarked(latLng);
					setAddress(address);
					form.setFieldsValue({ address });
				} else {
					console.error('Geocoder failed:', status);
				}
			});
		}
	};

	// Calculate map bounds based on directions overview path
	const calculateBounds = () => {
		if (!props.tripRoutes[props.activeIndex].directions || !props.tripRoutes[props.activeIndex].directions.routes || !props.tripRoutes[props.activeIndex].routes[0].overview_path) {
			return null;
		}

		const bounds = new window.google.maps.LatLngBounds();
		const path = props.tripRoutes[props.activeIndex].directions.routes[0].overview_path;

		path.forEach(point => bounds.extend(point));

		return bounds;
	};

	// Update map center and zoom based on bounds
	const fitBounds = () => {
		if (map && props.directions) {
			const bounds = calculateBounds();
			if (bounds) {
				map.fitBounds(bounds);
			}
		}
	};

	const handleAutocompletePlaceChanged = () => {
		if (autocomplete) {
			const place = autocomplete.getPlace();
			const exactInput = document.querySelector('.pac-target-input')?.value;

			if (place.geometry && place.geometry.location) {
				const location = place.geometry.location;
				const address = exactInput || place.formatted_address || place.name;
				setAddress(address);
				setCurrentMarked({ lat: location.lat(), lng: location.lng() });
				setCenterCoordinates({ lat: location.lat(), lng: location.lng() });
				map.setCenter(location);

				form.setFieldsValue({ address });
			}
		}
	};

	const handleConfirm = () => {
		if (props.routeType === 'origin') {
			if (currentMarked) {
				const newTripRoutes = props.tripRoutes;
				newTripRoutes[props.activeIndex] = {
					...newTripRoutes[props.activeIndex],
					origin: [{ lat: currentMarked.lat, lng: currentMarked.lng }]
				};
				props.handleDirections(newTripRoutes);
				props.handleGetAddress(address, { lat: currentMarked.lat, lng: currentMarked.lng }, map);

				form.setFieldsValue({ address: null });
				setCurrentMarked(null);
				props.setIsModalOpen(false);
			} else if (props.origin.length > 0) {
				const newTripRoutes = props.tripRoutes;

				newTripRoutes[props.activeIndex] = {
					...newTripRoutes[props.activeIndex],
					origin: [{ lat: props.tripRoutes[props.activeIndex].origin[0].lat, lng: props.tripRoutes[props.activeIndex].origin[0].lng }]
				};

				props.handleDirections(newTripRoutes);
				props.handleGetAddress(address, { lat: props.tripRoutes[props.activeIndex].origin[0].lat, lng: props.tripRoutes[props.activeIndex].origin[0].lng }, map);
				form.setFieldsValue({ address: null });
				setCurrentMarked(null);
				props.setActiveIndex(null);
				props.setIsModalOpen(false);
			}
			props.setRouteType(null);
			setAddress(null);
			setAutocomplete(null);
		} else {
			if (currentMarked) {
				const newTripRoutes = props.tripRoutes;
				newTripRoutes[props.activeIndex] = {
					...newTripRoutes[props.activeIndex],
					destination: [{ lat: currentMarked.lat, lng: currentMarked.lng }]
				};

				props.handleDirections(newTripRoutes);
				props.handleGetAddress(address, { lat: currentMarked.lat, lng: currentMarked.lng }, map);
				form.setFieldsValue({ address: null });
				setCurrentMarked(null);
				props.setIsModalOpen(false);
			} else if (props.destination.length > 0) {
				const newTripRoutes = props.tripRoutes;

				newTripRoutes[props.activeIndex] = {
					...newTripRoutes[props.activeIndex],
					destination: [{ lat: props.tripRoutes[props.activeIndex].destination[0].lat, lng: props.tripRoutes[props.activeIndex].destination[0].lng }]
				};

				props.handleDirections(newTripRoutes);
				props.handleGetAddress(address, { lat: props.destination[0].lat, lng: props.destination[0].lng }, map);
				form.setFieldsValue({ address: null });
				setCurrentMarked(null);
				props.setActiveIndex(null);
				props.setIsModalOpen(false);
			}
			props.setRouteType(null);
			setAddress(null);
			setAutocomplete(null);
		}
	};

	const onLoadAutocomplete = (autocompleteInstance) => {
		setAutocomplete(autocompleteInstance);

		if (map) {
			const mapCenter = map.getCenter();
			const bounds = {
				north: mapCenter.lat() + 0.2,
				south: mapCenter.lat() - 0.2,
				east: mapCenter.lng() + 0.2,
				west: mapCenter.lng() - 0.2
			};

			autocompleteInstance.setBounds(bounds);
		}
	};



	const onMapIdle = () => {
		if (map && autocomplete) {
			const mapCenter = map.getCenter();
			const bounds = {
				north: mapCenter.lat() + 0.2,
				south: mapCenter.lat() - 0.2,
				east: mapCenter.lng() + 0.2,
				west: mapCenter.lng() - 0.2
			};

			autocomplete.setBounds(bounds); // Update bounds when the map moves
		}
	};

	return (
		<>
			<Modal
				title={props.routeType === 'origin' ? 'Starting Pick-up Point' : 'Destination'}
				footer={null}
				destroyOnClose={true}
				onCancel={e => {
					form.setFieldsValue({ address: null });
					props.setActiveIndex(null);
					props.setIsModalOpen(false);
					props.setRouteType(null);
					setCurrentMarked(null);
					setAutocomplete(null);
					setAddress(null);
				}}
				open={props.isModalOpen}
				width={props.windowWidth < 992 ? '80%' : '50%'}
			// height="50%"
			>
				{props.isModalOpen && (
					<Row>
						<Col
							span={22}
							offset={1}
						>
							<div
								className="search-input-container"
								style={{ display: 'flex', width: '100%' }}
							>
								<Autocomplete
									onLoad={onLoadAutocomplete}
									onPlaceChanged={handleAutocompletePlaceChanged}
								>
									<Input
										value={address}
										onChange={(e) => setAddress(e.target.value)}
										type="text"
										placeholder={props.routeType === 'origin' ? 'Pick-up Location' : 'Destination'}
										style={{ width: '100%' }}
									/>
								</Autocomplete>
							</div>
							{props.isLoaded && (
								<GoogleMap
									mapContainerStyle={{ width: '100%', height: '450px', marginTop: 10 }}
									center={centerCoordinates}
									zoom={18}
									onClick={handleMapClick}
									onLoad={map => {
										setMap(map);
									}}
									onUnmount={onUnmount}
									onIdle={onMapIdle}
								>
									{currentMarked && <Marker position={{ lat: currentMarked.lat, lng: currentMarked.lng }} />}
									{displayOriginMark && (
										<Marker position={{ lat: props.tripRoutes[props.activeIndex].origin[0].lat, lng: props.tripRoutes[props.activeIndex].origin[0].lng }} />
									)}
									{displayDestinationMark && (
										<Marker position={{ lat: props.tripRoutes[props.activeIndex].destination[0].lat, lng: props.tripRoutes[props.activeIndex].destination[0].lng }} />
									)}
								</GoogleMap>
							)}
							<div style={{ marginTop: 19, display: 'flex', justifyContent: 'end' }}>
								<Space>
									<Button
										onClick={() => {
											form.setFieldsValue({ address: null });
											setCurrentMarked(null);
											props.setActiveIndex(null);
											props.setIsModalOpen(false);
											setAutocomplete(null);
											setAddress(null);
										}}
									>
										Cancel
									</Button>
									<Button
										type="primary"
										onClick={handleConfirm}
									>
										Confirm
									</Button>
								</Space>
							</div>
						</Col>
					</Row>
				)}
			</Modal>
		</>
	);
};

export default ModalMap;
